import { useState, useEffect, useCallback } from 'react';
import { 
  useAccount, 
  useReadContract, 
  useWriteContract, 
  useWaitForTransactionReceipt, 
  usePublicClient 
} from 'wagmi';
import { parseUnits, formatUnits } from 'viem';
import { toast } from 'react-toastify';

import stakingAbi from '../abis/stakingAbi.json';
import erc20Abi from '../abis/erc20Abi.json';
import { hyperliquidEvmConfig, STAKING } from '../constants';
import { StakingData, StakingState } from '../types';

export function useStaking() {
  const { address, isConnected } = useAccount();
  const publicClient = usePublicClient();
  
  const [stakeAmount, setStakeAmount] = useState<string>('');
  const [unstakeAmount, setUnstakeAmount] = useState<string>('');
  const [stakingData, setStakingData] = useState<StakingData>({
    totalStaked: 0n,
    userStaked: 0n,
    userPoints: 0n,
    userBalance: 0n,
    userAllowance: 0n,
    rewardRatePerToken: 0n,
    lastUpdateTime: 0n,
    pointRatePerTokenPerSecond: 0n,
  });
  
  const [stakingState, setStakingState] = useState<StakingState>({
    isLoading: true,
    isApprovalPending: false,
    isApprovalMining: false,
    isStakingPending: false,
    isUnstakingPending: false,
    error: null,
    txHash: null,
    needsApproval: false,
  });

  // Referral data state
  const [referralData, setReferralData] = useState({
    userReferrer: null as string | null,
    hasSetReferrer: false,
    referralCount: 0,
    referredUsers: [] as string[],
    referredUsersWithPoints: [] as Array<{address: string, points: string}>,
  });

  // Contract addresses
  const stakingContractAddress = hyperliquidEvmConfig.contracts.stakingContract?.address as `0x${string}`;
  const tokenContractAddress = hyperliquidEvmConfig.contracts.tokenContract?.address as `0x${string}`;
  
  // Read total staked
  const { data: totalStaked, refetch: refetchTotalStaked } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'totalStaked',
    query: {
      enabled: !!stakingContractAddress,
    },
  });
  
  // Read user staked balance
  const { data: userStaked, refetch: refetchUserStaked } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'stakeBalance',
    args: [address as `0x${string}`],
    query: {
      enabled: !!stakingContractAddress && !!address,
    },
  });
  
  // Read user reward points
  const { data: userPoints, refetch: refetchUserPoints } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'getPoints',
    args: [address as `0x${string}`],
    query: {
      enabled: !!stakingContractAddress && !!address,
    },
  });
  
  // Read reward rate
  const { data: rewardPerToken, refetch: refetchRewardPerToken } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'rewardPerToken',
    query: {
      enabled: !!stakingContractAddress,
    },
  });
  
  // Read last update time
  const { data: lastUpdateTime, refetch: refetchLastUpdateTime } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'globalLastUpdateTime',
    query: {
      enabled: !!stakingContractAddress,
    },
  });
  
  // Read point rate per token per second
  const { data: pointRatePerTokenPerSecond, refetch: refetchPointRate } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'pointRatePerTokenPerSecond',
    query: {
      enabled: !!stakingContractAddress,
    },
  });
  
  // Read user token balance
  const { data: userBalance, refetch: refetchUserBalance } = useReadContract({
    address: tokenContractAddress,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [address as `0x${string}`],
    query: {
      enabled: !!tokenContractAddress && !!address,
    },
  });
  
  // Read user token allowance for staking contract
  const { data: userAllowance, refetch: refetchUserAllowance } = useReadContract({
    address: tokenContractAddress,
    abi: erc20Abi,
    functionName: 'allowance',
    args: [address as `0x${string}`, stakingContractAddress],
    query: {
      enabled: !!tokenContractAddress && !!stakingContractAddress && !!address,
    },
  });
  
  // Read user's referrer
  const { data: userReferrer, refetch: refetchUserReferrer } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'getReferrer',
    args: [address as `0x${string}`],
    query: {
      enabled: !!stakingContractAddress && !!address,
    },
  });

  // Read if user has set referrer
  const { data: hasSetReferrer, refetch: refetchHasSetReferrer } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'hasSetReferrer',
    args: [address as `0x${string}`],
    query: {
      enabled: !!stakingContractAddress && !!address,
    },
  });

  // Read user's referral count
  const { data: referralCount, refetch: refetchReferralCount } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'getReferralCount',
    args: [address as `0x${string}`],
    query: {
      enabled: !!stakingContractAddress && !!address,
    },
  });

  // Read user's referred users
  const { data: referredUsers, refetch: refetchReferredUsers } = useReadContract({
    address: stakingContractAddress,
    abi: stakingAbi,
    functionName: 'getReferredUsers',
    args: [address as `0x${string}`],
    query: {
      enabled: !!stakingContractAddress && !!address,
    },
  });
  
  // Write contract hooks
  const { 
    writeContract: writeApprove, 
    isPending: isApproveWritePending,
    data: approveTxHash,
  } = useWriteContract();
  
  const { 
    writeContract: writeStake, 
    isPending: isStakeWritePending,
    data: stakeTxHash,
  } = useWriteContract();
  
  const { 
    writeContract: writeUnstake, 
    isPending: isUnstakeWritePending,
    data: unstakeTxHash,
  } = useWriteContract();
  
  const { 
    writeContract: writeSetReferrer, 
    isPending: isSetReferrerWritePending,
    data: setReferrerTxHash,
  } = useWriteContract();
  
  const { 
    writeContract: writeStakeWithReferrer, 
    isPending: isStakeWithReferrerWritePending,
    data: stakeWithReferrerTxHash,
  } = useWriteContract();
  
  // Transaction receipt hooks
  const { isLoading: isApproveLoading } = useWaitForTransactionReceipt({
    hash: approveTxHash,
    query: {
      enabled: !!approveTxHash,
    },
  });
  
  const { isLoading: isStakeLoading } = useWaitForTransactionReceipt({
    hash: stakeTxHash,
    query: {
      enabled: !!stakeTxHash,
    },
  });
  
  const { isLoading: isUnstakeLoading } = useWaitForTransactionReceipt({
    hash: unstakeTxHash,
    query: {
      enabled: !!unstakeTxHash,
    },
  });
  
  const { isLoading: isSetReferrerLoading } = useWaitForTransactionReceipt({
    hash: setReferrerTxHash,
    query: {
      enabled: !!setReferrerTxHash,
    },
  });
  
  const { isLoading: isStakeWithReferrerLoading } = useWaitForTransactionReceipt({
    hash: stakeWithReferrerTxHash,
    query: {
      enabled: !!stakeWithReferrerTxHash,
    },
  });
  
  // Effect to update staking data
  useEffect(() => {
    if (
      totalStaked !== undefined &&
      rewardPerToken !== undefined &&
      lastUpdateTime !== undefined &&
      pointRatePerTokenPerSecond !== undefined
    ) {
      const currentStakeAmountParsed = parseUnits(stakeAmount || '0', STAKING.TOKEN_DECIMALS);
      const needsApprovalCheck = isConnected && userAllowance !== undefined
        ? (userAllowance as bigint) < currentStakeAmountParsed
        : false;

      setStakingData({
        totalStaked: totalStaked as bigint,
        userStaked: (isConnected && userStaked !== undefined) ? userStaked as bigint : 0n,
        userPoints: (isConnected && userPoints !== undefined) ? userPoints as bigint : 0n,
        userBalance: (isConnected && userBalance !== undefined) ? userBalance as bigint : 0n,
        userAllowance: (isConnected && userAllowance !== undefined) ? userAllowance as bigint : 0n,
        rewardRatePerToken: rewardPerToken as bigint,
        lastUpdateTime: lastUpdateTime as bigint,
        pointRatePerTokenPerSecond: pointRatePerTokenPerSecond as bigint,
      });

      setStakingState(prev => ({
        ...prev,
        isLoading: !(
          totalStaked !== undefined &&
          rewardPerToken !== undefined &&
          lastUpdateTime !== undefined &&
          pointRatePerTokenPerSecond !== undefined &&
          (!isConnected || (
            userStaked !== undefined &&
            userPoints !== undefined &&
            userBalance !== undefined &&
            userAllowance !== undefined
          ))
        ),
        needsApproval: needsApprovalCheck
      }));
    } else {
      setStakingState(prev => ({ ...prev, isLoading: true }));
    }
  }, [
    totalStaked,
    rewardPerToken,
    lastUpdateTime,
    pointRatePerTokenPerSecond,
    userStaked,
    userPoints,
    userBalance,
    userAllowance,
    stakeAmount,
    isConnected
  ]);
  
  // Effect to update referral data
  useEffect(() => {
    if (isConnected && address) {
      const fetchReferralData = async () => {
        try {
          let referredUsersWithPoints: Array<{address: string, points: string}> = [];
          
          if (referredUsers && Array.isArray(referredUsers) && referredUsers.length > 0) {
            referredUsersWithPoints = await Promise.all(
              referredUsers.map(async (userAddr: string, index: number) => {
                try {
                  // This would need to be implemented with a separate contract call
                  // For now, we'll use a placeholder
                  const points = '0'; // Placeholder - would need to call getReferredUserPoints
                  return {
                    address: userAddr,
                    points: points
                  };
                } catch (error) {
                  console.error('Error fetching points for referred user:', error);
                  return {
                    address: userAddr,
                    points: '0'
                  };
                }
              })
            );
          }

          setReferralData({
            userReferrer: userReferrer ? (userReferrer as string) : null,
            hasSetReferrer: hasSetReferrer ? (hasSetReferrer as boolean) : false,
            referralCount: referralCount ? Number(referralCount) : 0,
            referredUsers: referredUsers ? (referredUsers as string[]) : [],
            referredUsersWithPoints: referredUsersWithPoints,
          });
        } catch (error) {
          console.error('Error updating referral data:', error);
        }
      };

      fetchReferralData();
    }
  }, [
    isConnected,
    address,
    userReferrer,
    hasSetReferrer,
    referralCount,
    referredUsers
  ]);
  
  // Effect to track transaction states
  useEffect(() => {
    setStakingState(prev => ({
      ...prev,
      isApprovalPending: isApproveWritePending,
      isApprovalMining: isApproveLoading,
      isStakingPending: isStakeWritePending || isStakeLoading,
      isUnstakingPending: isUnstakeWritePending || isUnstakeLoading,
      txHash: approveTxHash || stakeTxHash || unstakeTxHash || null,
    }));
  }, [
    isApproveWritePending, isApproveLoading,
    isStakeWritePending, isStakeLoading,
    isUnstakeWritePending, isUnstakeLoading,
    approveTxHash, stakeTxHash, unstakeTxHash
  ]);
  
  // Refetch all data function
  const refetchAllData = useCallback(() => {
    refetchTotalStaked();
    refetchUserStaked();
    refetchUserPoints();
    refetchUserBalance();
    refetchUserAllowance();
    refetchRewardPerToken();
    refetchLastUpdateTime();
    refetchPointRate();
    refetchUserReferrer();
    refetchHasSetReferrer();
    refetchReferralCount();
    refetchReferredUsers();
  }, [
    refetchTotalStaked,
    refetchUserStaked,
    refetchUserPoints,
    refetchUserBalance,
    refetchUserAllowance,
    refetchRewardPerToken,
    refetchLastUpdateTime,
    refetchPointRate,
    refetchUserReferrer,
    refetchHasSetReferrer,
    refetchReferralCount,
    refetchReferredUsers
  ]);
  
  // Listen for events on the contracts
  useEffect(() => {
    if (!publicClient || !stakingContractAddress || !address) return;
    
    const unwatch = publicClient.watchContractEvent({
      address: stakingContractAddress,
      abi: stakingAbi,
      eventName: 'Staked',
      onLogs: () => {
        refetchAllData();
        // toast.success("Tokens staked successfully!");
      },
    });
    
    const unwatchUnstake = publicClient.watchContractEvent({
      address: stakingContractAddress,
      abi: stakingAbi,
      eventName: 'Unstaked',
      onLogs: () => {
        refetchAllData();
        // toast.success("Tokens unstaked successfully!");
      },
    });
    
    const unwatchPoints = publicClient.watchContractEvent({
      address: stakingContractAddress,
      abi: stakingAbi,
      eventName: 'PointsUpdated',
      onLogs: () => {
        refetchUserPoints();
      },
    });

    const unwatchReferrerSet = publicClient.watchContractEvent({
      address: stakingContractAddress,
      abi: stakingAbi,
      eventName: 'ReferrerSet',
      onLogs: () => {
        refetchAllData();
        // toast.success("Referrer set successfully!");
      },
    });
    
    return () => {
      unwatch();
      unwatchUnstake();
      unwatchPoints();
      unwatchReferrerSet();
    };
  }, [publicClient, stakingContractAddress, address, refetchAllData, refetchUserPoints]);
  
  // Set referrer function
  const setReferrer = useCallback(async (referrerAddress: string) => {
    if (!stakingContractAddress || !address) {
      toast.error("Missing contract information. Please try again.");
      return;
    }
    
    try {
      setStakingState(prev => ({ ...prev, error: null }));
      writeSetReferrer({
        address: stakingContractAddress,
        abi: stakingAbi,
        functionName: 'setReferrer',
        args: [referrerAddress as `0x${string}`],
      });
    } catch (error) {
      console.error("Set referrer error:", error);
      setStakingState(prev => ({ 
        ...prev, 
        error: "Failed to set referrer. Please try again." 
      }));
      toast.error("Failed to set referrer");
    }
  }, [stakingContractAddress, address, writeSetReferrer]);
  
  // Approve token spending
  const approveTokens = useCallback(async () => {
    if (!tokenContractAddress || !stakingContractAddress || !address) {
      toast.error("Missing contract information. Please try again.");
      return;
    }
    
    try {
      setStakingState(prev => ({ ...prev, error: null }));
      writeApprove({
        address: tokenContractAddress,
        abi: erc20Abi,
        functionName: 'approve',
        args: [stakingContractAddress, BigInt(STAKING.DEFAULT_APPROVAL_AMOUNT)],
      });
    } catch (error) {
      console.error("Approval error:", error);
      setStakingState(prev => ({ 
        ...prev, 
        error: "Failed to approve token spending. Please try again." 
      }));
      toast.error("Failed to approve token spending");
    }
  }, [tokenContractAddress, stakingContractAddress, address, writeApprove]);
  
  // Stake tokens
  const stakeTokens = useCallback(async () => {
    if (!stakingContractAddress || !address || !stakeAmount) {
      toast.error("Missing information. Please enter amount and try again.");
      return;
    }
    
    try {
      const amountToStake = parseUnits(stakeAmount, STAKING.TOKEN_DECIMALS);
      
      // Check if amount exceeds balance
      if (userBalance && amountToStake > (userBalance as bigint)) {
        toast.error("Amount exceeds your balance");
        return;
      }
      
      // Check if approval is needed
      if (userAllowance && amountToStake > (userAllowance as bigint)) {
        toast.info("Please approve token spending first");
        setStakingState(prev => ({ ...prev, needsApproval: true }));
        return;
      }
      
      setStakingState(prev => ({ ...prev, error: null }));
      writeStake({
        address: stakingContractAddress,
        abi: stakingAbi,
        functionName: 'stake',
        args: [amountToStake],
      });
    } catch (error) {
      console.error("Staking error:", error);
      setStakingState(prev => ({ 
        ...prev, 
        error: "Failed to stake tokens. Please try again." 
      }));
      toast.error("Failed to stake tokens");
    }
  }, [stakingContractAddress, address, stakeAmount, userBalance, userAllowance, writeStake]);

  // Stake tokens with referrer
  const stakeTokensWithReferrer = useCallback(async (referrerAddress: string) => {
    if (!stakingContractAddress || !address || !stakeAmount) {
      toast.error("Missing information. Please enter amount and try again.");
      return;
    }
    
    try {
      const amountToStake = parseUnits(stakeAmount, STAKING.TOKEN_DECIMALS);
      
      // Check if amount exceeds balance
      if (userBalance && amountToStake > (userBalance as bigint)) {
        toast.error("Amount exceeds your balance");
        return;
      }
      
      // Check if approval is needed
      if (userAllowance && amountToStake > (userAllowance as bigint)) {
        toast.info("Please approve token spending first");
        setStakingState(prev => ({ ...prev, needsApproval: true }));
        return;
      }
      
      setStakingState(prev => ({ ...prev, error: null }));
      writeStakeWithReferrer({
        address: stakingContractAddress,
        abi: stakingAbi,
        functionName: 'stake',
        args: [amountToStake, referrerAddress as `0x${string}`],
      });
    } catch (error) {
      console.error("Staking with referrer error:", error);
      setStakingState(prev => ({ 
        ...prev, 
        error: "Failed to stake tokens with referrer. Please try again." 
      }));
      toast.error("Failed to stake tokens with referrer");
    }
  }, [stakingContractAddress, address, stakeAmount, userBalance, userAllowance, writeStakeWithReferrer]);
  
  // Unstake tokens
  const unstakeTokens = useCallback(async () => {
    if (!stakingContractAddress || !address || !unstakeAmount) {
      toast.error("Missing information. Please enter amount and try again.");
      return;
    }
    
    try {
      const amountToUnstake = parseUnits(unstakeAmount, STAKING.TOKEN_DECIMALS);
      
      // Check if amount exceeds staked balance
      if (userStaked && amountToUnstake > (userStaked as bigint)) {
        toast.error("Amount exceeds your staked balance");
        return;
      }
      
      setStakingState(prev => ({ ...prev, error: null }));
      writeUnstake({
        address: stakingContractAddress,
        abi: stakingAbi,
        functionName: 'unstake',
        args: [amountToUnstake],
      });
    } catch (error) {
      console.error("Unstaking error:", error);
      setStakingState(prev => ({ 
        ...prev, 
        error: "Failed to unstake tokens. Please try again." 
      }));
      toast.error("Failed to unstake tokens");
    }
  }, [stakingContractAddress, address, unstakeAmount, userStaked, writeUnstake]);
  
  // Format token amount for display
  const formatTokenAmount = useCallback((amount: bigint) => {
    return formatUnits(amount, STAKING.TOKEN_DECIMALS);
  }, []);
  
  return {
    stakingData,
    stakingState,
    referralData,
    stakeAmount,
    unstakeAmount,
    setStakeAmount,
    setUnstakeAmount,
    approveTokens,
    stakeTokens,
    stakeTokensWithReferrer,
    unstakeTokens,
    setReferrer,
    formatTokenAmount,
    refetchAllData,
  };
} 